import React, { useState } from 'react'
import { InputGroup, Intent, Icon } from '@blueprintjs/core'
import { Card } from '../ui/card'
import { IconNames } from '@blueprintjs/icons'
import type { Conversation } from '../../types/copilot'
import { Button } from '../ui/button'

interface ConversationListProps {
    conversations: Conversation[]
    currentConversationId?: number | null
    onConversationSelect: (conversationId: number) => void
    onConversationUpdate: (
        conversationId: number,
        update: { title?: string; is_archived?: boolean },
    ) => void
    onConversationDelete: (conversationId: number) => void
}

export function ConversationList({
    conversations,
    currentConversationId,
    onConversationSelect,
    onConversationUpdate,
}: ConversationListProps) {
    const [editingId, setEditingId] = useState<number | null>(null)
    const [editTitle, setEditTitle] = useState('')

    const handleEditStart = (conversation: Conversation) => {
        setEditingId(conversation.id)
        setEditTitle(conversation.title || '')
    }

    const handleEditSave = (conversationId: number) => {
        onConversationUpdate(conversationId, { title: editTitle })
        setEditingId(null)
        setEditTitle('')
    }

    const handleEditCancel = () => {
        setEditingId(null)
        setEditTitle('')
    }

    const formatDate = (dateString: string) => {
        const date = new Date(dateString)
        const now = new Date()
        const diffTime = Math.abs(now.getTime() - date.getTime())
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

        if (diffDays === 0) {
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        } else if (diffDays === 1) {
            return 'Yesterday'
        } else if (diffDays < 7) {
            return `${diffDays} days ago`
        } else {
            return date.toLocaleDateString()
        }
    }

    return (
        <div className="">
            {conversations.map((conversation) => (
                <div
                    key={conversation.id}
                    className={`group relative px-4 py-2 cursor-pointer transition-all duration-200 hover:shadow-md border-l-4 border-r-0 border-t-0 border-b-0 ${
                        currentConversationId === conversation.id
                            ? 'border-l-blue-500 bg-blue-50 shadow-sm'
                            : 'border-l-transparent bg-transparent hover:border-l-gray-300'
                    }`}
                    onClick={() => onConversationSelect(conversation.id)}
                >
                    <div className="space-y-2">
                        {editingId === conversation.id ? (
                            <div className="space-y-3">
                                <InputGroup
                                    value={editTitle}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        setEditTitle(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleEditSave(conversation.id)
                                        } else if (e.key === 'Escape') {
                                            handleEditCancel()
                                        }
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    autoFocus
                                />
                                <div className="flex space-x-2">
                                    <Button
                                        small
                                        intent={Intent.SUCCESS}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleEditSave(conversation.id)
                                        }}
                                        text="Save"
                                    />
                                    <Button
                                        small
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleEditCancel()
                                        }}
                                        text="Cancel"
                                    />
                                </div>
                            </div>
                        ) : (
                            <>
                                <h3 className="text-gray-900 truncate leading-tight">
                                    {conversation.title || 'Untitled'}
                                </h3>
                            </>
                        )}
                    </div>

                    {editingId !== conversation.id && (
                        <div className="absolute top-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <Button
                                small
                                minimal
                                icon={IconNames.EDIT}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    handleEditStart(conversation)
                                }}
                            />
                            <Button
                                small
                                minimal
                                icon={
                                    conversation.is_archived ? IconNames.INBOX : IconNames.ARCHIVE
                                }
                                onClick={(e) => {
                                    e.stopPropagation()
                                    const message = conversation.is_archived
                                        ? 'Are you sure you want to unarchive this conversation?'
                                        : 'Are you sure you want to archive this conversation?'

                                    if (confirm(message)) {
                                        onConversationUpdate(conversation.id, {
                                            is_archived: !conversation.is_archived,
                                        })
                                    }
                                }}
                            />
                        </div>
                    )}
                </div>
            ))}
        </div>
    )
}
